import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";
import {
  getChatAttachmentKind,
  getChatLinkTitle,
  isInternalAppPath,
  resolveChatLinkUrl,
  resolveChatMediaUrl,
} from "../../utils/chatbotMedia";
import { isModifiedClick } from "../../utils/spaNavigation";

const newTabProps = { target: "_blank", rel: "noopener noreferrer" };

const normalizeAttachments = (attachments, apiBaseUrl) =>
  (Array.isArray(attachments) ? attachments : [])
    .map((attachment, index) => {
      const url = resolveChatMediaUrl(attachment?.url, apiBaseUrl);
      if (!url) {
        return null;
      }

      return {
        key: `${attachment.attachmentId ?? "media"}-${index}`,
        url,
        fileName: String(attachment.fileName || "").trim(),
        caption: String(attachment.caption || "").trim(),
        kind: getChatAttachmentKind(attachment),
      };
    })
    .filter(Boolean);

const normalizeLinks = (links) =>
  (Array.isArray(links) ? links : [])
    .map((link, index) => {
      const url = resolveChatLinkUrl(link?.url);
      if (!url) {
        return null;
      }

      return {
        key: `${index}-${url}`,
        url,
        title: getChatLinkTitle(link.title, url),
        isInternal: isInternalAppPath(url),
      };
    })
    .filter(Boolean);

const ImageThumbnail = ({ image, compact, onOpen }) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const alt = image.caption || image.fileName || t("chatbot_image");

  if (failed) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          minWidth: 0,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          color: "text.secondary",
        }}
      >
        <BrokenImageOutlinedIcon fontSize="small" />
        <Typography component="div" variant="caption" noWrap title={alt}>
          {t("chatbot_imageUnavailable")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="figure" sx={{ m: 0, minWidth: 0 }}>
      <ButtonBase
        onClick={onOpen}
        aria-label={t("chatbot_viewImage", { name: alt })}
        sx={{
          display: "block",
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          component="img"
          src={image.url}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          sx={{
            display: "block",
            width: "100%",
            height: compact ? 100 : "auto",
            maxHeight: 160,
            objectFit: "cover",
            bgcolor: "action.hover",
          }}
        />
      </ButtonBase>
      {image.caption && (
        <Typography
          component="figcaption"
          variant="caption"
          color="text.secondary"
          noWrap={compact}
          title={image.caption}
          sx={{ display: "block", mt: 0.5 }}
        >
          {image.caption}
        </Typography>
      )}
    </Box>
  );
};

const ImageLightbox = ({ images, index, onClose, onNavigate }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isRtl = theme.direction === "rtl";
  const image = images[index];
  const total = images.length;
  const hasMultiple = total > 1;
  const alt = image.caption || image.fileName || t("chatbot_image");
  const PreviousIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon;
  const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const showRelative = (step) => onNavigate((index + step + total) % total);

  const handleKeyDown = (event) => {
    if (!hasMultiple) {
      return;
    }
    if (event.key === "ArrowRight") {
      showRelative(isRtl ? -1 : 1);
    } else if (event.key === "ArrowLeft") {
      showRelative(isRtl ? 1 : -1);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      onKeyDown={handleKeyDown}
      PaperProps={{ "aria-label": alt }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" noWrap title={image.fileName || alt} sx={{ flexGrow: 1, minWidth: 0 }}>
          {image.fileName || alt}
        </Typography>
        {hasMultiple && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {t("chatbot_imageCounter", { current: index + 1, total })}
          </Typography>
        )}
        <Tooltip title={t("chatbot_openInNewTab")}>
          <IconButton
            component="a"
            href={image.url}
            {...newTabProps}
            size="small"
            aria-label={t("chatbot_openInNewTab")}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label={t("chatbot_closePreview")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: { xs: 1, sm: 2 },
        }}
      >
        {hasMultiple && (
          <IconButton onClick={() => showRelative(-1)} aria-label={t("chatbot_previousImage")}>
            <PreviousIcon />
          </IconButton>
        )}
        <Box component="figure" sx={{ m: 0, flexGrow: 1, minWidth: 0, textAlign: "center" }}>
          <Box
            component="img"
            src={image.url}
            alt={alt}
            sx={{
              display: "inline-block",
              maxWidth: "100%",
              maxHeight: fullScreen ? "calc(100vh - 150px)" : "75vh",
              objectFit: "contain",
            }}
          />
          {image.caption && (
            <Typography component="figcaption" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {image.caption}
            </Typography>
          )}
        </Box>
        {hasMultiple && (
          <IconButton onClick={() => showRelative(1)} aria-label={t("chatbot_nextImage")}>
            <NextIcon />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DocumentCard = ({ file, canPreview, onPreview }) => {
  const { t } = useTranslation();
  const isPdf = file.kind === "pdf";
  const displayName = file.fileName || t(isPdf ? "chatbot_pdfDocument" : "chatbot_file");

  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {isPdf ? (
          <PictureAsPdfOutlinedIcon sx={{ color: "#C62828", flexShrink: 0 }} />
        ) : (
          <InsertDriveFileOutlinedIcon color="action" sx={{ flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography component="div" variant="body2" noWrap title={displayName} sx={{ fontWeight: 500 }}>
            {displayName}
          </Typography>
          {file.caption && (
            <Typography component="div" variant="caption" color="text.secondary" noWrap title={file.caption}>
              {file.caption}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.5, mt: 0.5 }}>
        {isPdf && canPreview && (
          <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onPreview(file)}>
            {t("chatbot_preview")}
          </Button>
        )}
        <Button size="small" component="a" href={file.url} {...newTabProps} startIcon={<OpenInNewIcon />}>
          {t("chatbot_openInNewTab")}
        </Button>
      </Box>
    </Paper>
  );
};

const ChatMessageAttachments = ({ attachments, links, apiBaseUrl = "/api", onInternalLinkClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const media = useMemo(() => normalizeAttachments(attachments, apiBaseUrl), [attachments, apiBaseUrl]);
  const normalizedLinks = useMemo(() => normalizeLinks(links), [links]);

  if (media.length === 0 && normalizedLinks.length === 0) {
    return null;
  }

  const images = media.filter((item) => item.kind === "image");
  const files = media.filter((item) => item.kind !== "image");

  // <embed> PDF previews are blank on most phones, so those users only get "open in new tab".
  const canPreviewPdfInline =
    !isSmallScreen && !(typeof navigator !== "undefined" && navigator.pdfViewerEnabled === false);

  const handleInternalLinkClick = (event, url) => {
    if (isModifiedClick(event)) {
      return;
    }
    event.preventDefault();
    onInternalLinkClick(url);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5, mb: 1, minWidth: { xs: 170, sm: 220 } }}>
      {images.length > 0 && (
        <Box
          role="group"
          aria-label={t("chatbot_attachmentsLabel")}
          sx={{
            display: "grid",
            gridTemplateColumns: images.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {images.map((image, index) => (
            <ImageThumbnail
              key={image.key}
              image={image}
              compact={images.length > 1}
              onOpen={() => setLightboxIndex(index)}
            />
          ))}
        </Box>
      )}

      {files.map((file) => (
        <DocumentCard key={file.key} file={file} canPreview={canPreviewPdfInline} onPreview={setPreviewFile} />
      ))}

      {normalizedLinks.length > 0 && (
        <Box role="group" aria-label={t("chatbot_linksLabel")} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {normalizedLinks.map((link) => {
            const handledInApp = link.isInternal && typeof onInternalLinkClick === "function";
            return (
              <Chip
                key={link.key}
                component="a"
                href={link.url}
                clickable
                size="small"
                variant="outlined"
                color="primary"
                icon={handledInApp ? <LinkIcon fontSize="small" /> : <OpenInNewIcon fontSize="small" />}
                label={link.title}
                title={link.title}
                {...(handledInApp
                  ? { onClick: (event) => handleInternalLinkClick(event, link.url) }
                  : newTabProps)}
                sx={{ maxWidth: "100%" }}
              />
            );
          })}
        </Box>
      )}

      {lightboxIndex !== null && images[lightboxIndex] && (
        <ImageLightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}

      {previewFile && (
        <PreviewAttachmentDialog
          open
          handleClose={() => setPreviewFile(null)}
          attachmentDetails={{ url: previewFile.url, fileName: previewFile.fileName, kind: previewFile.kind }}
          title={t("chatbot_previewTitle")}
          closeLabel={t("chatbot_closePreview")}
          showOpenInNewTab
          openInNewTabLabel={t("chatbot_openInNewTab")}
          fullScreenOnMobile
        />
      )}
    </Box>
  );
};

export default ChatMessageAttachments;
